/* eslint-disable max-lines */
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { Button, FormControlLabel, Grid, Radio, Skeleton, Typography } from "@mui/material";
import type {
  Metier,
  Territoire,
  Poste,
  Commune,
  CommuneAssocieeDeleguee,
  PerimetreWithoutTerritoire,
  BatchCreateContactMetierDto,
  CreateContactMetierDto,
  PerimetreAvecCommunesOuCads,
  ContactMetier,
  ContactMetierQueryFilter,
} from "models";
import { MetierNiveau } from "models";
import {
  useErrorHandler,
  useSnackbarErrorHandler,
  withPageErrorBoundary,
} from "utils/errorHandling";
import type { SubmitHandler } from "react-hook-form";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { onFormErrorDefault, sortingUtil } from "utils";
import {
  ControlledMetierAutoComplete,
  ControlledPosteAutoComplete,
  PerimetreMultiAutoWithErrorBoundary,
} from "components/inputs";
import { enqueueSnackbar } from "notistack";
import { ToastMessages } from "enums";
import { contactsMetiersService, perimetresService } from "services";
import { useMetiers, usePostes } from "hooks";
import { PageLayout } from "components/Layout";
import { ControlledPerimetreMultiAutoComplete } from "components/inputs/ControlledPerimetreMultiAutoComplete";
import { ControlledTerritoireMultiAutoComplete } from "components/inputs/ControlledTerritoireMultiAutoComplete";
import { ControlledRadioGroup } from "components/inputs/ControlledRadioGroup";
import { ControlledCommuneAssocieeDelegueeMultiAutoComplete } from "components/inputs/ControlledCommuneAssocieeDelegueeMultiAutoComplete";
import { ControlledCommuneMultiAutoComplete } from "components/inputs/ControlledCommuneMultiAutoComplete";
import { RecapitulatifDeselectionnableTerritoire } from "./components/RecapitulatifDeselectionnableTerritoire";
import { RecapitulatifDeselectionnablePerimetre } from "./components/RecapitulatifDeselectionnablePerimetre";
import { RecapitulatifDeselectionnableCad } from "./components/RecapitulatifDeselectionnableCad";
import { RecapitulatifDeselectionnableCommune } from "./components/RecapitulatifDeselectionnableCommune";
import { RecapitulatifInteractifPerimetreAvecCommunes } from "./components/RecapitulatifInteractifPerimetreAvecCommunes";
import { arrayUtil } from "@sdeapps/react-core";
import { CreationEnMasseConfirmationDialog } from "./components/CreationEnMasseConfirmationDialog";

interface SaisieEnMasseContactMetierFormModel {
  metier: Metier | null;
  typeSelection: "commune" | "commune-associee-deleguee" | null;
  communeSelection: "une-par-une" | "par-perimetres" | null;
  communes: Array<Commune>;
  cads: Array<CommuneAssocieeDeleguee>;
  perimetres: Array<PerimetreWithoutTerritoire>;
  territoires: Array<Territoire>;
  poste: Poste | null;
}

function formModelToBatchCreateContactMetierDto(
  formModel: SaisieEnMasseContactMetierFormModel
): BatchCreateContactMetierDto {
  let contacts: Array<CreateContactMetierDto> = [];
  switch (formModel.metier?.niveau) {
    case MetierNiveau.Territoire:
      contacts = (formModel.territoires ?? []).map((t) => ({
        $type: "territoire",
        metier: formModel.metier ?? undefined,
        poste: formModel.poste ?? undefined,
        territoire: t,
      }));
      break;
    case MetierNiveau.Perimetre:
      contacts = (formModel.perimetres ?? []).map((p) => ({
        $type: "perimetre",
        metier: formModel.metier ?? undefined,
        poste: formModel.poste ?? undefined,
        perimetre: p,
      }));
      break;
    case MetierNiveau.Commune:
      if (formModel.typeSelection === "commune-associee-deleguee") {
        contacts = (formModel.cads ?? []).map((cad) => ({
          $type: "commune-associee-deleguee",
          metier: formModel.metier ?? undefined,
          poste: formModel.poste ?? undefined,
          cad,
        }));
      } else if (formModel.typeSelection === "commune") {
        contacts = (formModel.communes ?? []).map((commune) => ({
          $type: "commune",
          metier: formModel.metier ?? undefined,
          poste: formModel.poste ?? undefined,
          commune,
        }));
      }
      break;
    default:
      throw new Error("Le Métier sélectionné est invalide");
  }
  const batchCreateContactsMetiersDto: BatchCreateContactMetierDto = { contacts };
  return batchCreateContactsMetiersDto;
}

const defaultValues: SaisieEnMasseContactMetierFormModel = {
  metier: null,
  typeSelection: null,
  communeSelection: null,
  communes: [],
  cads: [],
  perimetres: [],
  territoires: [],
  poste: null,
};

function SaisieEnMasseContactMetier(): ReactNode {
  const { metiers, isMetiersLoading } = useMetiers();
  const { postes, isPostesLoading } = usePostes();
  const [similarContactsMetiers, setSimilarContactsMetiers] = useState<Array<ContactMetier>>([]);

  const [perimetresAvecCommunes, setPerimetresAvecCommunes] = useState<
    Array<PerimetreAvecCommunesOuCads>
  >([]);
  const [selectedPerimetresAvecCommunes, setSelectedPerimetresAvecCommunes] = useState<
    Array<PerimetreAvecCommunesOuCads>
  >([]);
  const {
    catchErrors: catchPerimetresAvecCommunesErrors,
    isLoading: isPerimetresAvecCommunesLoading,
  } = useErrorHandler();
  useEffect(() => {
    async function getPerimetres(): Promise<void> {
      const perimetresAvecCommunes = await perimetresService.getPerimetresAvecCommunes();
      perimetresAvecCommunes.sort(sortingUtil.sortPerimetres);
      setPerimetresAvecCommunes(perimetresAvecCommunes);
    }
    void catchPerimetresAvecCommunesErrors(getPerimetres);
  }, [catchPerimetresAvecCommunesErrors]);

  const formMethods = useForm<SaisieEnMasseContactMetierFormModel>({ defaultValues });
  const { handleSubmit, control, setValue, reset } = formMethods;

  const {
    metier,
    typeSelection,
    communeSelection,
    poste,
    territoires,
    perimetres,
    cads,
    communes,
  } = useWatch({ control });

  useEffect(() => {
    if (metier?.niveau !== MetierNiveau.Territoire) setValue("territoires", []);
    if (metier?.niveau !== MetierNiveau.Perimetre) setValue("perimetres", []);
    if (metier?.niveau !== MetierNiveau.Commune) {
      setValue("communes", []);
      setValue("cads", []);
      setValue("typeSelection", null);
      setValue("communeSelection", null);
    }
    if (typeSelection !== "commune") {
      setValue("communes", []);
      setValue("communeSelection", null);
    }
    if (typeSelection !== "commune-associee-deleguee") setValue("cads", []);
    if (communeSelection !== "par-perimetres") setValue("communes", []);
    if (communeSelection !== "une-par-une") setSelectedPerimetresAvecCommunes([]);
  }, [metier?.niveau, typeSelection, communeSelection, setValue]);

  useEffect(() => {
    setValue(
      "communes",
      arrayUtil.dedupArray(
        selectedPerimetresAvecCommunes?.map((p) => p.communes).flat() ?? [],
        (c) => c.id
      )
    );
  }, [selectedPerimetresAvecCommunes, setValue]);

  const isCollectiviteRenseignee = useMemo(
    () =>
      (territoires != null && territoires?.length > 0) ||
      (perimetres != null && perimetres?.length > 0) ||
      (cads != null && cads?.length > 0) ||
      (communes != null && communes?.length > 0),
    [territoires, perimetres, cads, communes]
  );

  async function bulkCreateContactsMetiers(
    batchCreateContactsMetiersDto: BatchCreateContactMetierDto
  ): Promise<void> {
    await contactsMetiersService.batchCreate(batchCreateContactsMetiersDto);

    enqueueSnackbar({
      variant: "success",
      autoHideDuration: 2000,
      message: ToastMessages.CONTACT_METIER_BATCH_CREATION_SUCCESS,
    });

    setBatchCreateContactMetierDto(undefined);
    setSimilarContactsMetiers([]);
    reset({ ...defaultValues });
  }

  const [batchCreateContactMetierDto, setBatchCreateContactMetierDto] =
    useState<BatchCreateContactMetierDto>();
  const [isBatchCreateDialogOpen, setIsBatchCreateDialogOpen] = useState(false);

  const { catchErrors: catchSendingErrors, isLoading: isSending } = useSnackbarErrorHandler();
  const { catchErrors: catchFetchSimilarErrors, isLoading: isFetchSimilarLoading } =
    useSnackbarErrorHandler();
  const sendData: SubmitHandler<SaisieEnMasseContactMetierFormModel> = async function (
    formModel: SaisieEnMasseContactMetierFormModel
  ): Promise<void> {
    await catchFetchSimilarErrors(async () => {
      // On cherche s'il existe des contacts métiers similaires pour prévenir l'utilisateur
      const similarFilter: ContactMetierQueryFilter = {
        communesId: formModel.communes.map(({ id }) => id),
        communesAssocieesDelegueesId: formModel.cads.map(({ id }) => id),
        perimetresId: formModel.perimetres.map(({ id }) => id),
        territoiresId: formModel.territoires.map(({ id }) => id),
        metiersCode: formModel.metier?.code != null ? [formModel.metier.code] : undefined,
      };
      const { values: _similarCms } = await contactsMetiersService.getByFilter(similarFilter);
      setSimilarContactsMetiers(_similarCms);

      setBatchCreateContactMetierDto(formModelToBatchCreateContactMetierDto(formModel));
      setIsBatchCreateDialogOpen(true);
    });
  };

  return (
    <PageLayout pageTitle="Création en masse de Contacts Métiers">
      <FormProvider {...formMethods}>
        <Grid
          component="form"
          container
          spacing={2}
          sx={{ mb: 2 }}
          size={12}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={handleSubmit(sendData, onFormErrorDefault)}>
          <Grid
            container
            size={12}
            rowSpacing={2}
            sx={{
              ".Mui-checked + .MuiFormControlLabel-label": {
                color: "primary.main",
                fontWeight: "500",
              },
            }}>
            <Typography>
              Cette page vous permet de créer plusieurs Contacts Métiers en une seule fois. <br />
              Pour cela, commencez par sélectionner le Métier des Contacts Métiers à créer, puis
              renseignez les Collectivités concernées et enfin le Poste.
              <br />
              Un Contact Métier sera créé pour chaque collectivité sélectionnée.
            </Typography>

            <Grid size={12} container spacing={2}>
              <Typography variant="subtitle2" color="primary.dark" component={Grid} size={12}>
                Commencez par sélectionner le métier des nouveaux Contacts Métiers :
              </Typography>

              <Grid size={12}>
                {isMetiersLoading ? (
                  <Skeleton height={56} />
                ) : (
                  <ControlledMetierAutoComplete
                    name="metier"
                    label="Métier *"
                    metiers={metiers}
                    rules={{ required: "Veuillez sélectionner un Métier" }}
                    groupesParNiveau
                  />
                )}
              </Grid>
            </Grid>

            {metier != null && metier.niveau === MetierNiveau.Perimetre && (
              <Grid size={12} container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }} container sx={{ alignContent: "start" }}>
                  <Typography variant="subtitle2" color="primary.dark" component={Grid} size={12}>
                    Puis sélectionnez les Périmètres concernés :
                  </Typography>

                  <ControlledPerimetreMultiAutoComplete
                    name="perimetres"
                    label="Périmètres *"
                    rules={{ required: "Veuillez sélectionner un ou plusieurs Périmètres" }}
                  />
                </Grid>
                <RecapitulatifDeselectionnablePerimetre
                  size={{ xs: 12, md: 6 }}
                  sx={{ alignContent: "start" }}
                  perimetres={perimetres}
                  onPerimetreChange={(_perimetres) => {
                    setValue("perimetres", _perimetres);
                  }}
                />
              </Grid>
            )}

            {metier != null && metier.niveau === MetierNiveau.Territoire && (
              <Grid size={12} container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }} container sx={{ alignContent: "start" }}>
                  <Typography variant="subtitle2" color="primary.dark" component={Grid} size={12}>
                    Puis sélectionnez les Territoires concernés :
                  </Typography>

                  <ControlledTerritoireMultiAutoComplete
                    name="territoires"
                    label="Territoires *"
                    rules={{ required: "Veuillez sélectionner un ou plusieurs Territoires" }}
                  />
                </Grid>
                <RecapitulatifDeselectionnableTerritoire
                  size={{ xs: 12, md: 6 }}
                  sx={{ alignContent: "start" }}
                  territoires={territoires}
                  onTerritoireChange={(_territoires) => {
                    setValue("territoires", _territoires);
                  }}
                />
              </Grid>
            )}

            {metier != null && metier.niveau === MetierNiveau.Commune && (
              <Grid size={12} container spacing={0}>
                <Typography variant="subtitle2" color="primary.dark" component={Grid} size={12}>
                  Vos nouveaux Contacts Métiers concernent :
                </Typography>

                <ControlledRadioGroup fullWidth row name="typeSelection" defaultValue={null}>
                  <FormControlLabel value="commune" control={<Radio />} label="des Communes" />
                  <FormControlLabel
                    value="commune-associee-deleguee"
                    control={<Radio />}
                    label="des Communes associées/déléguées"
                  />
                </ControlledRadioGroup>
              </Grid>
            )}

            {metier != null &&
              metier.niveau === MetierNiveau.Commune &&
              typeSelection === "commune-associee-deleguee" && (
                <Grid size={12} container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }} container sx={{ alignContent: "start" }}>
                    <ControlledCommuneAssocieeDelegueeMultiAutoComplete
                      name="cads"
                      label="Communes associées/déléguées *"
                      rules={{
                        required:
                          "Veuillez sélectionner une ou plusieurs communes associées/déléguées",
                      }}
                    />
                  </Grid>
                  <RecapitulatifDeselectionnableCad
                    size={{ xs: 12, md: 6 }}
                    sx={{ alignContent: "start" }}
                    cads={cads}
                    onCommuneChange={(_cads) => {
                      setValue("cads", _cads);
                    }}
                  />
                </Grid>
              )}

            {metier != null &&
              metier.niveau === MetierNiveau.Commune &&
              typeSelection === "commune" && (
                <Grid size={12} container spacing={0}>
                  <Typography variant="subtitle2" color="primary.dark" component={Grid} size={12}>
                    Comment voulez-vous sélectionner les communes ?
                  </Typography>

                  <ControlledRadioGroup fullWidth row name="communeSelection" defaultValue={null}>
                    <FormControlLabel value="une-par-une" control={<Radio />} label="une par une" />
                    <FormControlLabel
                      value="par-perimetres"
                      control={<Radio />}
                      label="par appartenance à des périmètres"
                    />
                  </ControlledRadioGroup>
                </Grid>
              )}

            {metier != null &&
              metier.niveau === MetierNiveau.Commune &&
              typeSelection === "commune" &&
              communeSelection === "une-par-une" && (
                <Grid size={12} container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }} container sx={{ alignContent: "start" }}>
                    <ControlledCommuneMultiAutoComplete
                      name="communes"
                      label="Communes *"
                      rules={{ required: "Veuillez sélectionner une ou plusieurs communes" }}
                    />
                  </Grid>
                  <RecapitulatifDeselectionnableCommune
                    size={{ xs: 12, md: 6 }}
                    sx={{ alignContent: "start" }}
                    communes={communes}
                    onCommuneChange={(_communes) => {
                      setValue("communes", _communes);
                    }}
                  />
                </Grid>
              )}

            {metier != null &&
              metier.niveau === MetierNiveau.Commune &&
              typeSelection === "commune" &&
              communeSelection === "par-perimetres" && (
                <Grid size={12} container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }} container sx={{ alignContent: "start" }}>
                    {isPerimetresAvecCommunesLoading ? (
                      <Skeleton height={56} />
                    ) : (
                      <PerimetreMultiAutoWithErrorBoundary
                        label="Périmètres *"
                        value={selectedPerimetresAvecCommunes}
                        onChange={(_perimetres) => {
                          // On garde tous les périmètres actuellement sélectionnés dans leur état actuel (sélection de leurs communes)
                          const _selectedPerimetres = selectedPerimetresAvecCommunes.filter((p) =>
                            _perimetres.some((_p) => _p.id === p.id)
                          );

                          // On cherche la présence d'un périmètre qui n'était pas précédemment sélectionné
                          const missingPerimetreId = _perimetres.find(
                            (p) => !_selectedPerimetres.some((_p) => _p.id === p.id)
                          )?.id;
                          const missingPerimetre = perimetresAvecCommunes.find(
                            (per) => missingPerimetreId === per.id
                          );
                          // Si on en trouve un, on l'ajoute aux périmètres sélectionnés en sélectionnant toutes ses communes
                          if (missingPerimetre != null) {
                            _selectedPerimetres.push(missingPerimetre);
                          }

                          setSelectedPerimetresAvecCommunes(_selectedPerimetres);
                        }}
                      />
                    )}
                  </Grid>
                  <RecapitulatifInteractifPerimetreAvecCommunes
                    size={{ xs: 12, md: 6 }}
                    sx={{ alignContent: "start" }}
                    allPerimetres={perimetresAvecCommunes}
                    perimetresSelectionnes={selectedPerimetresAvecCommunes}
                    onPerimetreChange={(_perimetresAvecCommunes) => {
                      setSelectedPerimetresAvecCommunes(_perimetresAvecCommunes);
                    }}
                  />
                </Grid>
              )}
          </Grid>

          {metier != null && isCollectiviteRenseignee && (
            <Grid size={12} container spacing={2}>
              <Typography variant="subtitle2" color="primary.dark" component={Grid} size={12}>
                Enfin, sélectionnez le poste :
              </Typography>

              <Grid size={12}>
                {isPostesLoading ? (
                  <Skeleton height={56} />
                ) : (
                  <ControlledPosteAutoComplete
                    name="poste"
                    label="Poste *"
                    postes={postes}
                    rules={{ required: "Veuillez sélectionner un Poste" }}
                  />
                )}
              </Grid>
            </Grid>
          )}

          <Grid container size={12} sx={{ justifyContent: "flex-end" }}>
            <Button color="error" href="/">
              Annuler
            </Button>
            <Button
              variant="contained"
              type="submit"
              loading={isSending || isFetchSimilarLoading}
              startIcon={<PersonAddIcon />}
              loadingPosition="start"
              disabled={metier == null || !isCollectiviteRenseignee || poste == null}>
              Créer les Contacts Métiers
            </Button>
          </Grid>
        </Grid>
      </FormProvider>
      <CreationEnMasseConfirmationDialog
        isOpen={isBatchCreateDialogOpen}
        onClose={() => {
          setIsBatchCreateDialogOpen(false);
          setBatchCreateContactMetierDto(undefined);
        }}
        contactsMetiersToCreate={batchCreateContactMetierDto}
        similarContactsMetiers={similarContactsMetiers}
        onConfirm={() => {
          void catchSendingErrors(async () => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            await bulkCreateContactsMetiers(batchCreateContactMetierDto!);
          });
        }}
      />
    </PageLayout>
  );
}

export const SaisieEnMasseContactMetierPageWithErrorBoundary = withPageErrorBoundary(
  SaisieEnMasseContactMetier
);
